import { IconAtom2Outline18 as Atom } from "nucleo-ui-outline-18"

import type { ReportingQueryProps } from "@/screens/app/reporting/reporting-query"
import { ReportingAreaChart } from "@/components/reporting/charts"
import { api } from "@/lib/api"

interface AiResolutionRateChartProps extends ReportingQueryProps {
	className?: string
}

interface TooltipPayload {
	active?: boolean
	payload?: Array<{
		value: number
		payload: {
			date: string
			total: number
			aiResolved: number
			rate: number
		}
	}>
	label?: string
}

const ResolutionTooltip = ({ active, payload }: TooltipPayload) => {
	if (!active || !payload?.length) return null
	const point = payload[0]?.payload
	if (!point) return null
	return (
		<div className="rounded-lg bg-white px-3 py-2 text-xs ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
			<p className="mb-1.5 font-medium text-primary">{point.date}</p>
			<p className="text-tertiary">
				AI resolved {point.aiResolved} of {point.total}
			</p>
			<p className="font-medium text-primary">Resolution rate {point.rate}%</p>
		</div>
	)
}

export const AiResolutionRateChart = ({
	className,
	startDate,
	endDate,
	...filters
}: AiResolutionRateChartProps) => {
	const query = api.workspace.getAiResolutionRate.useQuery(
		{ startDate, endDate, ...filters },
		{ cacheTime: Infinity, staleTime: Infinity },
	)

	const data = query.data
	const hasData = (data?.totals.totalThreads ?? 0) > 0

	return (
		<ReportingAreaChart
			title="AI resolution rate"
			description="Share of conversations resolved by the Productlane Agent without a teammate replying."
			className={className}
			isLoading={!data}
			data={hasData ? (data?.chartData ?? []) : []}
			series={[{ key: "rate", label: "Resolution rate", tone: "primary" }]}
			icon={<Atom className="size-3.5" />}
			legend={
				data ? (
					<span className="text-xs text-tertiary">
						{data.totals.rate}% of {data.totals.totalThreads} threads
					</span>
				) : undefined
			}
			yDomain={[0, 100]}
			yTickFormatter={(v) => `${v}%`}
			tooltip={<ResolutionTooltip />}
		/>
	)
}
